import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { StepIndicator } from '../ui/StepIndicator';
import { RelationshipPills, InterestsPills, BudgetPills, OccasionPills } from '../ui/PillSelector';
import { useConfetti } from '../ui/Confetti';
import { usePostHog } from 'posthog-js/react';

/**
 * PersonForm Component
 *
 * A conversational, step-by-step wizard for adding a new person.
 * One question at a time - never overwhelm.
 */

const STEPS = [
  { id: 'name', title: 'Who do you want to remember?' },
  { id: 'relationship', title: 'How do you know them?' },
  { id: 'occasion', title: "What's the special occasion?" },
  { id: 'date', title: 'When is the occasion?' },
  { id: 'interests', title: 'What makes them smile?' },
  { id: 'budget', title: "What's your typical budget?" },
  { id: 'complete', title: '' }, // Completion screen
];

export function PersonForm({
  onComplete,
  onCancel,
  initialData = {},
  editId = null, // ID for editing existing person
  autoCloseDelay = 2000, // Auto-close completion screen after this delay (ms)
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    recipientName: '',
    relationship: '',
    occasion: 'birthday',
    date: '',
    interests: [],
    interestsNote: '', // Free text describing what makes them smile
    budget: '',
    ...initialData,
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const { trigger: triggerConfetti, Confetti } = useConfetti();
  const posthog = usePostHog();

  // Auto-close after reaching completion screen
  useEffect(() => {
    if (STEPS[currentStep]?.id === 'complete' && !isSaving) {
      const timer = setTimeout(() => {
        onComplete?.(formData, editId);
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [currentStep, formData, onComplete, autoCloseDelay, isSaving, editId]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateStep = () => {
    const step = STEPS[currentStep];

    switch (step.id) {
      case 'name':
        if (!formData.recipientName.trim()) {
          setErrors({ recipientName: 'Please enter a name' });
          return false;
        }
        break;
      case 'relationship':
        if (!formData.relationship) {
          setErrors({ relationship: 'Please select a relationship' });
          return false;
        }
        break;
      case 'occasion':
        if (!formData.occasion) {
          setErrors({ occasion: 'Please select an occasion' });
          return false;
        }
        break;
      case 'date':
        if (!formData.date) {
          setErrors({ date: 'Please select a date' });
          return false;
        }
        // Validate date is in the future
        const selectedDate = new Date(formData.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          setErrors({ date: 'Please select a future date' });
          return false;
        }
        break;
      // interests and budget are optional
    }

    return true;
  };

  const handleNext = () => {
    const step = STEPS[currentStep];

    // If on completion screen, manually trigger complete (user clicked button instead of waiting)
    if (step.id === 'complete') {
      onComplete?.(formData, editId);
      return;
    }

    if (!validateStep()) return;

    if (currentStep < STEPS.length - 1) {
      const nextStepId = STEPS[currentStep + 1]?.id;
      posthog.capture('person_step_completed', { step: STEPS[currentStep].id, step_number: currentStep + 1 });
      setCurrentStep((prev) => prev + 1);
      // Trigger confetti when reaching completion screen
      if (nextStepId === 'complete') {
        posthog.capture('person_added', {
          relationship: formData.relationship,
          occasion: formData.occasion,
          has_interests: formData.interests.length > 0,
          has_budget: !!formData.budget,
        });
        triggerConfetti(50, 40);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else {
      onCancel?.();
    }
  };

  const handleSkip = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const renderStep = () => {
    const step = STEPS[currentStep];

    switch (step.id) {
      case 'name':
        return (
          <div className="space-y-6">
            <Input
              placeholder="Mom, Best Friend Sarah, Partner..."
              value={formData.recipientName}
              onChange={(e) => updateField('recipientName', e.target.value)}
              error={errors.recipientName}
              autoFocus
              className="text-xl py-4"
            />
          </div>
        );

      case 'relationship':
        return (
          <div className="space-y-4">
            <RelationshipPills
              selected={formData.relationship}
              onChange={(value) => updateField('relationship', value)}
            />
            {errors.relationship && (
              <p className="text-sm text-red-500">{errors.relationship}</p>
            )}

            {/* Sub-options for family */}
            {formData.relationship === 'family' && (
              <div className="mt-4 pt-4 border-t border-warm-100">
                <p className="text-sm text-warm-500 mb-3">More specifically...</p>
                <div className="flex flex-wrap gap-2">
                  {['Parent', 'Sibling', 'Child', 'Grandparent', 'Cousin', 'In-law'].map((type) => (
                    <button
                      key={type}
                      onClick={() => updateField('relationshipDetail', type)}
                      className={`
                        pill
                        ${formData.relationshipDetail === type ? 'pill-selected' : ''}
                      `}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'occasion':
        return (
          <div className="space-y-4">
            <p className="text-slate-500 text-sm mb-2">
              What occasion do you want to remember for {formData.recipientName || 'them'}?
            </p>
            <OccasionPills
              selected={formData.occasion}
              onChange={(value) => updateField('occasion', value)}
            />
            {errors.occasion && (
              <p className="text-sm text-red-500">{errors.occasion}</p>
            )}
          </div>
        );

      case 'date':
        // Full calendar picker - dates starting from today + 7 days
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const minDate = new Date(today);
        minDate.setDate(today.getDate() + 7); // Start from 7 days from now

        // Calendar state - use formData.date to determine initial month or minDate's month
        const getInitialMonth = () => {
          if (formData.date) {
            return new Date(formData.date + 'T00:00:00');
          }
          return minDate; // Start showing the month where minDate falls
        };

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Get calendar month from a hidden state via formData or default to today's month
        const calendarMonth = formData._calendarMonth
          ? new Date(formData._calendarMonth)
          : getInitialMonth();

        const getDaysInMonth = (date) => {
          const year = date.getFullYear();
          const month = date.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          return { daysInMonth: lastDay.getDate(), startingDay: firstDay.getDay() };
        };

        const { daysInMonth, startingDay } = getDaysInMonth(calendarMonth);

        const prevMonth = () => {
          const newMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
          updateField('_calendarMonth', newMonth.toISOString());
        };

        const nextMonth = () => {
          const newMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
          updateField('_calendarMonth', newMonth.toISOString());
        };

        const isDateSelectable = (day) => {
          const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
          date.setHours(0, 0, 0, 0);
          return date >= minDate; // Only dates from today + 7 onwards
        };

        const isDateSelected = (day) => {
          if (!formData.date) return false;
          const selectedDate = new Date(formData.date + 'T00:00:00');
          return day === selectedDate.getDate() &&
                 calendarMonth.getMonth() === selectedDate.getMonth() &&
                 calendarMonth.getFullYear() === selectedDate.getFullYear();
        };

        const isTodayDate = (day) => {
          return day === today.getDate() &&
                 calendarMonth.getMonth() === today.getMonth() &&
                 calendarMonth.getFullYear() === today.getFullYear();
        };

        const handleDateClick = (day) => {
          if (isDateSelectable(day)) {
            const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
            updateField('date', date.toISOString().split('T')[0]);
          }
        };

        // Can go to previous month only if it's not before minDate's month
        const canGoPrev = calendarMonth.getFullYear() > minDate.getFullYear() ||
          (calendarMonth.getFullYear() === minDate.getFullYear() && calendarMonth.getMonth() > minDate.getMonth());

        return (
          <div className="space-y-3">
            {/* Compact Calendar Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Month Navigation - Compact */}
              <div className="bg-gradient-to-r from-coral-500 to-rose-500 px-3 py-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevMonth}
                  disabled={!canGoPrev}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-white transition-colors ${canGoPrev ? 'bg-white/20 hover:bg-white/30' : 'opacity-30 cursor-not-allowed'}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-semibold text-white">
                  {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={nextMonth}
                  className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Day Names - Single letter, compact */}
              <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                  <div key={i} className="py-1 text-center text-xs font-medium text-slate-400">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid - Compact cells */}
              <div className="grid grid-cols-7 p-1 gap-0.5">
                {/* Empty cells for days before the 1st */}
                {Array.from({ length: startingDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-8" />
                ))}

                {/* Day cells */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const selectable = isDateSelectable(day);
                  const selected = isDateSelected(day);
                  const isToday = isTodayDate(day);

                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDateClick(day)}
                      disabled={!selectable}
                      className={`
                        h-8 w-full rounded flex items-center justify-center text-xs font-medium transition-all
                        ${selected
                          ? 'bg-coral-500 text-white'
                          : selectable
                            ? 'hover:bg-coral-50 text-slate-700'
                            : 'text-slate-300'
                        }
                        ${isToday && !selected ? 'ring-1 ring-coral-400 bg-coral-50' : ''}
                      `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Date Display - Below calendar */}
            {formData.date && (
              <div className="text-center py-2 px-3 bg-coral-50 rounded-lg">
                <p className="text-sm font-medium text-coral-600">
                  {new Date(formData.date + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}

            {errors.date && (
              <p className="text-sm text-red-500">{errors.date}</p>
            )}
          </div>
        );

      case 'interests':
        return (
          <div className="space-y-2">
            <InterestsPills
              selected={formData.interests}
              onChange={(value) => updateField('interests', value)}
            />

            {/* Compact free text input */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block mb-1">
                <span className="text-slate-500 text-xs">💭 Tell us more (optional)</span>
              </label>
              <textarea
                value={formData.interestsNote}
                onChange={(e) => updateField('interestsNote', e.target.value)}
                placeholder="Preferences, brands, sizes, colors..."
                rows={1}
                className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-coral-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
        );

      case 'budget':
        return (
          <div className="space-y-4">
            <p className="text-warm-500 text-sm">
              What do you usually spend on {formData.recipientName || 'them'}?
            </p>
            <BudgetPills
              selected={formData.budget}
              onChange={(value) => updateField('budget', value)}
            />
          </div>
        );

      case 'complete':
        const occasionName = formData.occasion
          ? formData.occasion.charAt(0).toUpperCase() + formData.occasion.slice(1)
          : 'special day';
        return (
          <div className="flex flex-col items-center justify-center text-center py-8 animate-scale-in">
            {/* Success Icon */}
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-xl shadow-emerald-500/30 animate-bounce-in">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              {/* Sparkles */}
              <div className="absolute -top-2 -right-2 text-2xl animate-float">✨</div>
              <div className="absolute -bottom-1 -left-3 text-xl animate-float" style={{ animationDelay: '0.5s' }}>🎁</div>
            </div>

            {/* Message */}
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              You're all set!
            </h2>
            <p className="text-slate-500 text-lg mb-6 max-w-xs">
              We'll find the perfect gift for <span className="font-semibold text-coral-600">{formData.recipientName}'s {occasionName}</span>
            </p>

            {/* Promise Card */}
            <div className="bg-gradient-to-r from-coral-50 to-coral-100/50 rounded-2xl p-5 border border-coral-200 w-full max-w-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">💝</span>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-800 text-sm">Our Promise</p>
                  <p className="text-slate-600 text-sm mt-1">
                    We'll curate thoughtful gift ideas, handle the purchase, and deliver it beautifully wrapped.
                  </p>
                </div>
              </div>
            </div>

            {/* Auto-close indicator */}
            <div className="mt-6 flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-coral-500 rounded-full animate-spin" />
              <span>Closing automatically...</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isOptionalStep = ['interests', 'budget'].includes(STEPS[currentStep]?.id);
  const isLastStep = currentStep === STEPS.length - 1;
  const isCompleteStep = STEPS[currentStep]?.id === 'complete';

  // Generate dynamic title for date step
  const getStepTitle = () => {
    const step = STEPS[currentStep];
    if (step.id === 'date' && formData.recipientName && formData.occasion) {
      const occasionDisplay = formData.occasion.charAt(0).toUpperCase() + formData.occasion.slice(1);
      return `When is ${formData.recipientName}'s ${occasionDisplay}?`;
    }
    return step.title;
  };

  return (
    <div className="flex flex-col h-full">
      <Confetti />

      {/* Progress - hide on completion */}
      {!isCompleteStep && (
        <div className="px-5 pt-4 pb-6">
          <StepIndicator steps={STEPS.length - 1} currentStep={currentStep} />
        </div>
      )}

      {/* Question - hide on completion */}
      {!isCompleteStep && (
        <div className="px-5 pb-4">
          <h2 className="font-display text-2xl text-warm-900">
            {getStepTitle()}
          </h2>
        </div>
      )}

      {/* Form content */}
      <div className="flex-1 px-5 overflow-y-auto">
        {renderStep()}
      </div>

      {/* Actions - fixed at bottom */}
      <div className={`px-5 py-4 bg-white flex-shrink-0 ${!isCompleteStep ? 'border-t border-warm-100' : ''}`}>
        {isCompleteStep ? (
          /* Completion screen - subtle skip button (auto-closes anyway) */
          <button
            onClick={handleNext}
            className="w-full py-3 text-slate-500 hover:text-coral-600 transition-colors text-sm"
          >
            Skip waiting →
          </button>
        ) : (
          /* Regular navigation buttons */
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="px-4"
            >
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </Button>

            <div className="flex-1" />

            {isOptionalStep && !isLastStep && (
              <Button
                variant="secondary"
                onClick={handleSkip}
              >
                Skip
              </Button>
            )}

            <Button
              variant="primary"
              onClick={handleNext}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * QuickAddPerson - Minimal form for quick adding
 */
export function QuickAddPerson({
  onComplete,
  onCancel,
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    onComplete?.({ recipientName: name.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="p-5">
      <h2 className="font-display text-xl text-warm-900 mb-4">
        Quick Add Person
      </h2>

      <Input
        placeholder="Enter name..."
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setError('');
        }}
        error={error}
        autoFocus
      />

      <div className="flex gap-3 mt-4">
        <Button variant="ghost" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="submit" className="flex-1">
          Add Person
        </Button>
      </div>

      <p className="text-xs text-warm-400 text-center mt-4">
        You can add more details later
      </p>
    </form>
  );
}

export default PersonForm;
